"use client";

import Footer from "@/components/common/Footer";
import LinearColorText from "@/components/common/LinearColorText";
import SOCIAL from "@/data/social.json";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Clock, Mail } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "@/components/common/Button";
import EarthCard from "@/components/home/Card/EarthCard";
import { sendContactEmail } from "./actions";
import { contactSchema, type ContactFormData } from "./schema";

type FormStatus = "idle" | "loading" | "success" | "error";

type ContactFormValues = ContactFormData & { company?: string };

const SUBJECTS = ["Nouveau projet", "Collaboration", "Consulting", "Autre"] as const;

function FieldError({ id, message }: { id: string; message?: string }) {
    if (!message) return null;
    return (
        <p id={id} role="alert" className="text-red-400 text-xs mt-1">
            {message}
        </p>
    );
}

export default function ContactPageClient() {
    const [status, setStatus] = useState<FormStatus>("idle");
    const [serverError, setServerError] = useState<string | null>(null);
    const formStartedAtRef = useRef(Date.now());

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = useForm<ContactFormValues>({
        resolver: zodResolver(contactSchema),
        defaultValues: { subject: "Nouveau projet", company: "" },
        mode: "onChange",
    });

    const onSubmit = async (data: ContactFormValues) => {
        setStatus("loading");
        setServerError(null);

        const result = await sendContactEmail({
            ...data,
            startedAt: formStartedAtRef.current,
        });

        if (result.success) {
            setStatus("success");
            reset();
        } else {
            setStatus("error");
            setServerError(result.error);
        }
    };

    const inputClass = (hasError: boolean) =>
        `w-full bg-surface-container-lowest border rounded-xl px-4 py-3.5 text-on-surface
         placeholder:text-on-surface-variant/40 focus:outline-none focus:ring-1 transition-all text-sm
         ${hasError
            ? "border-red-500/50 focus:border-red-500/60 focus:ring-red-500/20"
            : "border-white/8 focus:border-primary/40 focus:ring-primary/20"
        }`;

    return (
        <>
            <main className="container" id="main-content">
                <header className="space-y-4 max-w-3xl">
                    <div className="hero-badge inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/30 border border-green-500/50 max-w-fit">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-xs font-label text-green-400">Disponible pour de nouveaux projets</span>
                    </div>
                    <p className="text-xs font-mono text-on-surface-variant uppercase tracking-[0.2em]">
                        Travaillons ensemble
                    </p>
                    <h1 className="text-5xl md:text-7xl font-headline font-bold uppercase tracking-tight text-on-surface leading-[0.9]">
                        Construisons{" "}
                        <LinearColorText>quelque&nbsp;chose</LinearColorText> ensemble.
                    </h1>
                    <p className="text-on-surface-variant text-base md:text-lg leading-relaxed">
                        Une idée, un projet, une question ? Écrivez-moi, je réponds dans les 24h.
                    </p>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <section
                        aria-label="Formulaire de contact"
                        className="lg:col-span-7 bg-surface-container-low border border-white/10 rounded-3xl p-6"
                    >
                        {status === "success" ? (
                            <div
                                role="status"
                                aria-live="polite"
                                className="flex flex-col items-center justify-center text-center h-full py-16 gap-6"
                            >
                                <div
                                    aria-hidden="true"
                                    className="w-16 h-16 rounded-full bg-green-500/30 border border-green-500/20 flex items-center justify-center"
                                >
                                    <Check className="size-6 text-green-400" />
                                </div>
                                <div className="space-y-2">
                                    <h2 className="text-2xl font-headline font-bold text-on-surface">
                                        Message envoyé !
                                    </h2>
                                    <p className="text-on-surface-variant text-sm max-w-sm">
                                        Merci pour votre message. Je vous répondrai dans les 24h.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h2 className="font-headline text-xl font-bold text-on-surface mb-8">
                                    Envoyer un message
                                </h2>

                                {status === "error" && serverError && (
                                    <div
                                        role="alert"
                                        aria-live="assertive"
                                        className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"
                                    >
                                        {serverError}
                                    </div>
                                )}

                                <form
                                    onSubmit={handleSubmit(onSubmit)}
                                    noValidate
                                    aria-label="Formulaire de prise de contact"
                                    className="relative space-y-4"
                                >
                                    <div
                                        className="absolute -left-[9999px] top-0 w-px h-px overflow-hidden"
                                        aria-hidden="true"
                                    >
                                        <label htmlFor="contact-company">Company</label>
                                        <input
                                            id="contact-company"
                                            type="text"
                                            tabIndex={-1}
                                            autoComplete="off"
                                            {...register("company")}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label
                                                htmlFor="contact-name"
                                                className="block text-[10px] font-mono uppercase tracking-widest text-on-surface-variant"
                                            >
                                                Nom <span aria-hidden="true" className="text-red-400">*</span>
                                            </label>
                                            <input
                                                id="contact-name"
                                                type="text"
                                                autoComplete="name"
                                                aria-required="true"
                                                aria-describedby={errors.name ? "error-name" : undefined}
                                                aria-invalid={!!errors.name}
                                                placeholder="Votre nom"
                                                className={inputClass(!!errors.name)}
                                                {...register("name")}
                                            />
                                            <FieldError id="error-name" message={errors.name?.message} />
                                        </div>

                                        <div className="space-y-2">
                                            <label
                                                htmlFor="contact-email"
                                                className="block text-[10px] font-mono uppercase tracking-widest text-on-surface-variant"
                                            >
                                                Email <span aria-hidden="true" className="text-red-400">*</span>
                                            </label>
                                            <input
                                                id="contact-email"
                                                type="email"
                                                autoComplete="email"
                                                aria-required="true"
                                                aria-describedby={errors.email ? "error-email" : undefined}
                                                aria-invalid={!!errors.email}
                                                placeholder="votre@email.com"
                                                className={inputClass(!!errors.email)}
                                                {...register("email")}
                                            />
                                            <FieldError id="error-email" message={errors.email?.message} />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            htmlFor="contact-subject"
                                            className="block text-[10px] font-mono uppercase tracking-widest text-on-surface-variant"
                                        >
                                            Objet
                                        </label>
                                        <select
                                            id="contact-subject"
                                            aria-describedby={errors.subject ? "error-subject" : undefined}
                                            aria-invalid={!!errors.subject}
                                            className={`${inputClass(!!errors.subject)} appearance-none`}
                                            {...register("subject")}
                                        >
                                            {SUBJECTS.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                        <FieldError id="error-subject" message={errors.subject?.message} />
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            htmlFor="contact-message"
                                            className="block text-[10px] font-mono uppercase tracking-widest text-on-surface-variant"
                                        >
                                            Message <span aria-hidden="true" className="text-red-400">*</span>
                                        </label>
                                        <textarea
                                            id="contact-message"
                                            rows={5}
                                            aria-required="true"
                                            aria-describedby={errors.message ? "error-message" : undefined}
                                            aria-invalid={!!errors.message}
                                            placeholder="Décrivez votre projet ou votre demande..."
                                            className={`${inputClass(!!errors.message)} resize-none`}
                                            {...register("message")}
                                        />
                                        <FieldError id="error-message" message={errors.message?.message} />
                                    </div>

                                    <Button
                                        type="submit"
                                        size="sm"
                                        disabled={status === "loading" || !isValid}
                                        aria-busy={status === "loading"}
                                        variant="default"
                                        className="focus-visible:outline transition-all disabled:opacity-60 disabled:cursor-not-allowed bg-white text-black font-headline font-bold"
                                    >
                                        {status === "loading" ? (
                                            <>
                                                <span
                                                    className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"
                                                    aria-hidden="true"
                                                />
                                                <span>Envoi en cours…</span>
                                            </>
                                        ) : (
                                            "Envoyer"
                                        )}
                                    </Button>
                                </form>
                            </>
                        )}
                    </section>

                    <aside aria-label="Informations de contact" className="lg:col-span-5 flex flex-col gap-6 h-min">
                        <div>
                            <EarthCard className="max-h-48 h-full" />
                        </div>

                        <div className="card shrink-0 p-4 space-y-4">
                            <Link href="mailto:contact@benjamin-drouin.fr" className="flex p-2 border border-white/10 rounded-2xl items-center gap-2">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                                    <Mail className="size-4 text-primary" />
                                </div>
                                <div>
                                    <p className="text-on-surface-variant text-[11px] uppercase">Email</p>
                                    <p className="font-headline font-bold text-on-surface text-sm">contact@benjamin-drouin.fr</p>
                                </div>
                            </Link>
                            <div className="flex p-2 border border-white/10 rounded-xl items-center gap-2">
                                <div className="w-9 h-9 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center">
                                    <Clock className="size-4 text-secondary" />
                                </div>
                                <div>
                                    <p className="text-on-surface-variant text-[11px] uppercase">Réponse</p>
                                    <p className="font-headline font-bold text-on-surface text-sm">Dans les 24h</p>
                                </div>
                            </div>
                            <ul className="grid grid-cols-6 gap-4" aria-label="Liens vers les réseaux sociaux">
                                {SOCIAL.map((social) => (
                                    <Link
                                        key={social.name}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Visiter mon profil ${social.name} (nouvelle fenêtre)`}
                                    >
                                        <Button size="icon" variant="default" className="aspect-square flex items-center justify-center bg-background w-full cursor-pointer">
                                            <Image
                                                src={social.icon}
                                                alt={social.name}
                                                width={social.width}
                                                height={social.height}
                                                className="opacity-70 group-hover:opacity-100 transition-opacity"
                                            />
                                        </Button>
                                    </Link>
                                ))}
                            </ul>
                        </div>
                    </aside>
                </div>
            </main>
            <Footer />
        </>
    );
}
